import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';

export interface PageLoaderProps {
  fullScreen?: boolean;
  message?: string;
  text?: string;
  compact?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const PageLoader: React.FC<PageLoaderProps> = ({
  fullScreen = false,
  message,
  text,
  compact = false,
  size = 'md',
  className = '',
}) => {
  const tokens = useThemeTokens();
  const labelText = message || text || 'Hydrating Environment...';

  const iconSizes = {
    sm: 'w-5 h-5',
    md: 'w-10 h-10',
    lg: 'w-14 h-14',
  };

  const ringSizes = {
    sm: 'w-8 h-8',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
  };

  const loaderContent = (
    <div className={`flex flex-col items-center justify-center text-center animate-fade-in ${compact ? 'py-4' : 'py-16 md:py-24'} ${className}`}>
      <div className="relative flex items-center justify-center mb-5">
        {/* Ambient background pulse rings using theme tokens */}
        <div className={`absolute ${ringSizes[size]} rounded-full border-2 ${tokens.accentBorder} opacity-25 animate-ping pointer-events-none`} />
        <div className={`absolute ${ringSizes[size]} rounded-full bg-current ${tokens.accentText} opacity-10 blur-xl pointer-events-none`} />
        
        {/* Main Spinning Loader with theme accent color */}
        <Loader2 className={`${iconSizes[size]} ${tokens.accentText} animate-spin relative z-10`} strokeWidth={2.5} />
      </div>

      {labelText && (
        <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/80 shadow-xs">
          <Sparkles className={`w-3.5 h-3.5 ${tokens.accentText} animate-pulse`} />
          <p className={`text-[10px] font-black uppercase tracking-[0.25em] ${tokens.accentText} animate-pulse`}>
            {labelText}
          </p>
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-[75vh] w-full flex items-center justify-center p-6 bg-slate-50/60 dark:bg-slate-900/60 animate-fade-in">
        <div className={`p-8 md:p-12 rounded-3xl ${tokens.cardBg} border shadow-2xl max-w-sm w-full flex items-center justify-center text-center`}>
          {loaderContent}
        </div>
      </div>
    );
  }

  return loaderContent;
};

export default PageLoader;
